import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, MapPin, Globe2 } from "lucide-react";
import HotelOptionCard from "@/components/cards/HotelOptionCard";
import { SourcePill, HeaderAccessory } from "@/components/cards/_pills";

export default function HotelListCard({ card, onPlacesToSee }) {
    const {
        title,
        options = [],
        city,
        live,
        source = "travelpayouts",
        source_label = "Travelpayouts",
        affiliate_network,
        alt_network_label,
        destination_weather,
        hide_source_pill,
        translator,
    } = card;
    const [expanded, setExpanded] = useState(false);
    // Phase 16 — interactive hotel sorting (mirrors flight sort chips).
    const [sortMode, setSortMode] = useState("best");
    const shaped = useMemo(() => {
        const list = [...options];
        if (sortMode === "price") {
            list.sort((a, b) =>
                Number(a?.price_per_night?.amount ?? Infinity) -
                Number(b?.price_per_night?.amount ?? Infinity));
        } else if (sortMode === "rating") {
            list.sort((a, b) =>
                Number(b?.rating_score ?? -1) - Number(a?.rating_score ?? -1));
        }
        return list;
    }, [options, sortMode]);
    const visible = expanded ? shaped : shaped.slice(0, 4);
    const hiddenCount = Math.max(shaped.length - 4, 0);
    const destCity = city || options[0]?.city;

    return (
        <section className="space-y-3" data-testid="hotel-list">
            <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                <div className="flex items-center gap-2">
                    <h2 className="text-[18px] font-semibold tracking-tight text-[#0A0A0A]">
                        {title}
                    </h2>
                    {live && !hide_source_pill && (
                        <SourcePill
                            source={source}
                            source_label={source_label}
                            affiliate_network={affiliate_network}
                            alt_network_label={alt_network_label}
                        />
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {destCity && onPlacesToSee && (
                        <button
                            type="button"
                            onClick={() =>
                                onPlacesToSee(`things to do in ${destCity} for 3 days`)
                            }
                            data-testid="places-to-see-hotels"
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-3 py-1 text-[11.5px] font-medium text-[#0A0A0A] transition hover:border-[#F97316] hover:text-[#F97316]"
                        >
                            <MapPin size={11} strokeWidth={2} />
                            Places to see in {destCity}
                        </button>
                    )}
                    <HeaderAccessory
                        destinationWeather={destination_weather}
                        dotColor="#16A34A"
                    />
                </div>
            </div>
            {/* Phase 7A — contextual translator deep-link pill. Backend
                attaches `translator` only when destination's local
                language ≠ user locale. */}
            {translator && (
                <a
                    href={translator.deep_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="translator-pill"
                    className="inline-flex items-center gap-1.5 self-start rounded-full border border-[#E5E5E5] bg-white px-3 py-1 text-[11.5px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A]"
                >
                    <Globe2 size={11} strokeWidth={2} />
                    Open Translator ({translator.target_lang_label})
                </a>
            )}
            {/* Horizontal scroll row */}
            <div className="-mx-1 overflow-x-auto pb-2">
                {options.length > 1 && (
                    <div className="flex flex-wrap items-center gap-1.5 px-1" data-testid="hotel-sort-row">
                        {[["best", "Best"], ["price", "Cheapest"], ["rating", "Top rated"]].map(([mode, label]) => (
                            <button
                                key={mode}
                                type="button"
                                onClick={() => setSortMode(mode)}
                                data-testid={`hotel-sort-${mode}`}
                                className={`rounded-full border px-3 py-1 text-[12px] font-medium transition ${
                                    sortMode === mode
                                        ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                                        : "border-[#E5E5E5] bg-white text-[#0A0A0A] hover:border-[#0A0A0A]"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                )}
                <div className="flex gap-3 px-1">
                    {visible.map((h) => (
                        <HotelOptionCard key={h.id} hotel={h} />
                    ))}
                </div>
            </div>
            {hiddenCount > 0 && (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    data-testid="hotel-list-toggle"
                    className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-medium text-[#0A0A0A] transition hover:bg-[#F5F5F5]"
                >
                    {expanded ? (
                        <>
                            <ChevronUp size={14} strokeWidth={2.25} /> Hide extras
                        </>
                    ) : (
                        <>
                            <ChevronDown size={14} strokeWidth={2.25} /> More options (+{hiddenCount})
                        </>
                    )}
                </button>
            )}
        </section>
    );
}
